import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { displayError } from '@/lib/error';
import { toast } from './ui/use-toast';
import { useApplyVendorPayment } from '@/services/vendorPaymentService';
import {
  VENDOR_PAYMENT_MODE_OPTIONS,
  type ApplyVendorPaymentResponse,
  type VendorPaymentMode,
} from '@/types/vendorPayment.types';
import { VendorApplyPaymentSchema, type VendorApplyPaymentFormData } from '@pages/accounts/vendorPayment.schema';

interface VendorInvoiceForPayment {
  processBillingVendorEntryId: string;
  vendorInvoiceNumber: string;
  amount: number;
  vendorId: string;
}

interface VendorApplyPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoices: VendorInvoiceForPayment[];
  onSuccess?: (data: ApplyVendorPaymentResponse) => void;
}

const VendorApplyPaymentModal = ({ open, onOpenChange, invoices, onSuccess }: VendorApplyPaymentModalProps) => {
  const applyPayment = useApplyVendorPayment();

  const totalInvoiceAmount = useMemo(() => invoices.reduce((sum, inv) => sum + inv.amount, 0), [invoices]);

  const invoiceIds = useMemo(() => invoices.map((inv) => inv.processBillingVendorEntryId), [invoices]);

  const form = useForm<VendorApplyPaymentFormData>({
    resolver: zodResolver(VendorApplyPaymentSchema),
    mode: 'onChange',
    defaultValues: {
      paymentMode: '',
      paymentAmount: '0',
      checkNumber: '',
      invoiceIds: [],
    },
  });
  const { control, setValue, formState, getValues } = form;
  const paymentMode = useWatch({ control, name: 'paymentMode' });

  useEffect(() => {
    if (open) {
      form.reset({
        paymentMode: '',
        paymentAmount: totalInvoiceAmount.toFixed(2),
        checkNumber: '',
        invoiceIds,
      });
    }
  }, [open, invoiceIds, totalInvoiceAmount, form]);

  // Amount is auto-filled and always equal to total (no partial/over payments allowed).
  useEffect(() => {
    setValue('paymentAmount', totalInvoiceAmount.toFixed(2));
  }, [paymentMode, totalInvoiceAmount, setValue]);

  const handleSubmit = () => {
    const data = getValues();
    applyPayment.mutate(
      {
        invoiceIds,
        paymentMode: data.paymentMode as VendorPaymentMode,
        checkNumber: data.checkNumber?.trim() ? data.checkNumber.trim() : undefined,
        paymentAmount: parseFloat(data.paymentAmount ?? '0'),
      },
      {
        onSuccess: (response) => {
          toast({ description: response.message ?? 'Payment applied successfully' });
          onOpenChange(false);
          onSuccess?.(response);
        },
        onError: (error) => {
          displayError(error.response?.data);
        },
      }
    );
  };

  const invoiceNumbers = invoices.map((inv) => inv.vendorInvoiceNumber);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[774px] max-w-[774px] min-h-[484px] max-h-[90vh] flex flex-col overflow-hidden p-6">
        <div className="flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Apply Payment</DialogTitle>
            <DialogDescription className="sr-only">Apply payment to selected vendor invoices</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1 mt-3">
            <div className="text-sm text-gray-700 leading-relaxed">
              <span className="font-medium">Invoice #: </span>
              <span>{invoiceNumbers.join(', ')}</span>
            </div>
            <div className="text-sm text-gray-700">
              <span className="font-medium">Total Amount: </span>
              <span className="text-[#7B1FA2] font-semibold">{formatCurrency(totalInvoiceAmount)}</span>
            </div>
          </div>
        </div>

        <FormProvider {...form}>
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto py-4 custom-scrollbar">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <FormFieldSelect
                    name="paymentMode"
                    label="Mode of Payment"
                    placeholder="Select Mode of Payment"
                    options={VENDOR_PAYMENT_MODE_OPTIONS.map((opt) => ({
                      label: opt.label,
                      value: opt.value,
                    }))}
                    required
                    onChange={() => {
                      setValue('checkNumber', '');
                    }}
                    className="focus:border-[#1551B2]"
                  />
                </div>

                {(paymentMode === 'CHECK' || paymentMode === 'ECHECK') && (
                  <div className="col-span-6">
                    <FormFieldComponent
                      control={control}
                      name="checkNumber"
                      label="Check #"
                      placeholder="Enter Check Number"
                      required
                      maxLength={20}
                      allowSpecialCharacters={false}
                      replaceRegex={/[^0-9]/g}
                    />
                  </div>
                )}

                {paymentMode && (
                  <div className="col-span-6">
                    <FormFieldComponent
                      control={control}
                      name="paymentAmount"
                      label="Amount"
                      placeholder="Enter Amount"
                      required
                      disabled
                      type="number"
                      allowSpecialCharacters={false}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex-shrink-0 w-full flex justify-end gap-3 pt-2">
              <Button type="button" className="w-[75px] h-[36px]" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="default"
                className="w-[134px] h-[36px]"
                disabled={!formState.isValid || applyPayment.isPending}
              >
                {'Review and Pay'}
              </Button>
            </div>
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default VendorApplyPaymentModal;
